import { useState, useRef, useEffect } from 'react'
import { critterTypeApi } from '../services/critterTypeApi'
import '../styles/CreateCritterPanel.css'

const CreateCritterPanel = ({ isOpen, onClose, onTypeCreated, broadcasterLogin }) => {
  const [name, setName] = useState('')
  const [typeKey, setTypeKey] = useState('')
  const [mouthClosedFile, setMouthClosedFile] = useState(null)
  const [mouthOpenFile, setMouthOpenFile] = useState(null)
  const [smilingFile, setSmilingFile] = useState(null)
  const [mouthClosedPreview, setMouthClosedPreview] = useState(null)
  const [mouthOpenPreview, setMouthOpenPreview] = useState(null)
  const [smilingPreview, setSmilingPreview] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [customTypes, setCustomTypes] = useState([])
  const [isLoadingTypes, setIsLoadingTypes] = useState(false)

  const mouthClosedRef = useRef(null)
  const mouthOpenRef = useRef(null)
  const smilingRef = useRef(null)

  // Load custom types when panel opens
  useEffect(() => {
    if (isOpen && broadcasterLogin) {
      loadCustomTypes()
    }
  }, [isOpen, broadcasterLogin])

  const loadCustomTypes = async () => {
    setIsLoadingTypes(true)
    try {
      critterTypeApi.setBroadcasterLogin(broadcasterLogin)
      const types = await critterTypeApi.getCustomTypes()
      setCustomTypes(types)
    } catch (err) {
      console.error('Failed to load custom types:', err)
    } finally {
      setIsLoadingTypes(false)
    }
  }

  // Auto-generate typeKey from name
  useEffect(() => {
    if (name) {
      const key = name.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
      setTypeKey(key)
    }
  }, [name])

  const handleFileChange = (setter, previewSetter) => (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setter(file)
      const reader = new FileReader()
      reader.onload = (e) => previewSetter(e.target.result)
      reader.readAsDataURL(file)
    }
  }

  const clearFile = (setter, previewSetter, ref) => () => {
    setter(null)
    previewSetter(null)
    if (ref.current) ref.current.value = ''
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError('Please enter a critter name')
      return
    }

    if (!mouthClosedFile || !mouthOpenFile) {
      setError('Mouth closed and mouth open images are required')
      return
    }

    setIsSubmitting(true)

    try {
      critterTypeApi.setBroadcasterLogin(broadcasterLogin)
      await critterTypeApi.createType(typeKey, name, mouthClosedFile, mouthOpenFile, smilingFile)

      // Reset form
      setName('')
      setTypeKey('')
      setMouthClosedFile(null)
      setMouthOpenFile(null)
      setSmilingFile(null)
      setMouthClosedPreview(null)
      setMouthOpenPreview(null)
      setSmilingPreview(null)

      // Reload custom types
      await loadCustomTypes()

      // Notify parent
      if (onTypeCreated) {
        onTypeCreated()
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteType = async (typeKeyToDelete) => {
    if (!confirm(`Are you sure you want to delete this critter type?`)) {
      return
    }

    try {
      critterTypeApi.setBroadcasterLogin(broadcasterLogin)
      await critterTypeApi.deleteType(typeKeyToDelete)
      await loadCustomTypes()
      if (onTypeCreated) {
        onTypeCreated()
      }
    } catch (err) {
      setError(err.message)
    }
  }

  if (!isOpen) return null

  return (
    <div className="create-critter-overlay" onClick={onClose}>
      <div className="create-critter-panel" onClick={(e) => e.stopPropagation()}>
        <div className="create-critter-header">
          <h2>Create Custom Critter</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="create-critter-content">
          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit} className="create-critter-form">
            <div className="form-group">
              <label htmlFor="critterName">Critter Name</label>
              <input
                type="text"
                id="critterName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., My Cool Cat"
                maxLength={50}
              />
              {typeKey && <small className="type-key-preview">Type key: {typeKey}</small>}
            </div>

            <div className="image-uploads">
              <div className="image-upload-group">
                <label>Mouth Closed (Required)</label>
                <div
                  className={`upload-zone ${mouthClosedPreview ? 'has-image' : ''}`}
                  onClick={() => mouthClosedRef.current?.click()}
                >
                  {mouthClosedPreview ? (
                    <>
                      <img src={mouthClosedPreview} alt="Mouth closed preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={(e) => {
                          e.stopPropagation()
                          clearFile(setMouthClosedFile, setMouthClosedPreview, mouthClosedRef)()
                        }}
                      >
                        &times;
                      </button>
                    </>
                  ) : (
                    <span>Click to upload</span>
                  )}
                </div>
                <input
                  type="file"
                  ref={mouthClosedRef}
                  accept="image/*"
                  onChange={handleFileChange(setMouthClosedFile, setMouthClosedPreview)}
                  hidden
                />
              </div>

              <div className="image-upload-group">
                <label>Mouth Open (Required)</label>
                <div
                  className={`upload-zone ${mouthOpenPreview ? 'has-image' : ''}`}
                  onClick={() => mouthOpenRef.current?.click()}
                >
                  {mouthOpenPreview ? (
                    <>
                      <img src={mouthOpenPreview} alt="Mouth open preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={(e) => {
                          e.stopPropagation()
                          clearFile(setMouthOpenFile, setMouthOpenPreview, mouthOpenRef)()
                        }}
                      >
                        &times;
                      </button>
                    </>
                  ) : (
                    <span>Click to upload</span>
                  )}
                </div>
                <input
                  type="file"
                  ref={mouthOpenRef}
                  accept="image/*"
                  onChange={handleFileChange(setMouthOpenFile, setMouthOpenPreview)}
                  hidden
                />
              </div>

              <div className="image-upload-group">
                <label>Smiling (Optional)</label>
                <div
                  className={`upload-zone ${smilingPreview ? 'has-image' : ''}`}
                  onClick={() => smilingRef.current?.click()}
                >
                  {smilingPreview ? (
                    <>
                      <img src={smilingPreview} alt="Smiling preview" />
                      <button
                        type="button"
                        className="remove-image"
                        onClick={(e) => {
                          e.stopPropagation()
                          clearFile(setSmilingFile, setSmilingPreview, smilingRef)()
                        }}
                      >
                        &times;
                      </button>
                    </>
                  ) : (
                    <span>Click to upload</span>
                  )}
                </div>
                <input
                  type="file"
                  ref={smilingRef}
                  accept="image/*"
                  onChange={handleFileChange(setSmilingFile, setSmilingPreview)}
                  hidden
                />
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Critter Type'}
            </button>
          </form>

          {/* Custom Types List */}
          <div className="custom-types-section">
            <h3>Your Custom Critters</h3>
            {isLoadingTypes ? (
              <p className="loading-text">Loading...</p>
            ) : customTypes.length === 0 ? (
              <p className="no-types-text">No custom critters yet. Create one above!</p>
            ) : (
              <div className="custom-types-list">
                {customTypes.map((type) => (
                  <div key={type.type_key} className="custom-type-item">
                    <img
                      src={`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${type.mouth_closed_url}`}
                      alt={type.name}
                      className="type-preview"
                    />
                    <div className="type-info">
                      <span className="type-name">{type.name}</span>
                      <span className="type-key">{type.type_key}</span>
                    </div>
                    <button
                      className="delete-type-btn"
                      onClick={() => handleDeleteType(type.type_key)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateCritterPanel
